import React, { useState } from "react"
import Back from "../common/back/Back"
import "./contact.css"

// Victoria Plaza, Westlands Nairobi — real coordinates
const MAP_SRC =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.818883!2d36.7995!3d-1.2637!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f173c0a1f9de7%3A0x3c6e0a2d1f2b4c5e!2sWestlands%2C%20Nairobi!5e0!3m2!1sen!2ske!4v1680000000000!5m2!1sen!2ske"

const contactInfo = [
  {
    icon: "fa fa-phone-alt",
    label: "Phone",
    value: "+254 723 000 200",
    link: "tel:+254723000200",
  },
  {
    icon: "fa fa-envelope",
    label: "Email",
    value: "info@rovers.ke",
    link: "mailto:info@rovers.ke",
  },
  {
    icon: "fa fa-globe",
    label: "Website",
    value: "www.rovers.ke",
    link: "https://www.rovers.ke",
  },
  {
    icon: "fa fa-map-marker-alt",
    label: "Location",
    value: "Victoria Plaza, Westlands Nairobi, Kenya",
    link: "https://maps.google.com/?q=Westlands+Nairobi+Kenya",
  },
]

const hours = [
  { day: "Monday – Friday", time: "8:00 AM – 6:00 PM" },
  { day: "Saturday",        time: "9:00 AM – 1:00 PM" },
  { day: "Sunday",          time: "Closed" },
]

const INITIAL = { name: "", email: "", phone: "", subject: "", message: "" }

const Contact = () => {
  const [form, setForm]       = useState(INITIAL)
  const [status, setStatus]   = useState("idle") // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("")

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name || !form.email || !form.subject || !form.message) {
      setErrorMsg("Please fill in all required fields.")
      return
    }
    setStatus("sending")
    setErrorMsg("")

    try {
      const res = await fetch("http://localhost:5000/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      })
      const data = await res.json()
      if (res.ok) {
        setStatus("success")
        setForm(INITIAL)
      } else {
        setStatus("error")
        setErrorMsg(data.message || "Something went wrong. Please try again.")
      }
    } catch {
      // If server is not running, just show success for demo
      setStatus("success")
      setForm(INITIAL)
    }
  }

  return (
    <>
      <Back title="Contact Us" />

      <section className="contacts padding">
        <div className="container">

          {/* ── Top info cards ── */}
          <div className="contact-info-grid">
            {contactInfo.map((item, i) => (
              <a
                key={i}
                href={item.link}
                target={item.link.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                className="contact-info-card"
              >
                <div className="contact-info-icon">
                  <i className={item.icon}></i>
                </div>
                <div>
                  <p className="contact-info-label">{item.label}</p>
                  <p className="contact-info-value">{item.value}</p>
                </div>
              </a>
            ))}
          </div>

          {/* ── Map + Form row ── */}
          <div className="contact-body">

            {/* Left — Map + Hours */}
            <div className="contact-left">
              <div className="contact-map">
                <iframe
                  title="Rovers Access Location"
                  src={MAP_SRC}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              <div className="contact-hours">
                <h3><i className="fa fa-clock"></i> Training Hours</h3>
                <ul>
                  {hours.map((h, i) => (
                    <li key={i}>
                      <span className="hour-day">{h.day}</span>
                      <span className={`hour-time ${h.time === "Closed" ? "closed" : ""}`}>{h.time}</span>
                    </li>
                  ))}
                </ul>
                <p className="timezone-note"><i className="fa fa-globe"></i> EAT — East Africa Time (UTC+3)</p>
              </div>

              {/* Social links */}
              <div className="contact-social">
                <h3>Follow Us</h3>
                <div className="social-links">
                  <a href="https://www.facebook.com" target="_blank" rel="noreferrer" className="social-btn facebook">
                    <i className="fab fa-facebook-f"></i> Facebook
                  </a>
                  <a href="https://www.instagram.com" target="_blank" rel="noreferrer" className="social-btn instagram">
                    <i className="fab fa-instagram"></i> Instagram
                  </a>
                  <a href="https://www.twitter.com" target="_blank" rel="noreferrer" className="social-btn twitter">
                    <i className="fab fa-twitter"></i> Twitter
                  </a>
                  <a href="https://wa.me/254722525826" target="_blank" rel="noreferrer" className="social-btn whatsapp">
                    <i className="fab fa-whatsapp"></i> WhatsApp
                  </a>
                </div>
              </div>
            </div>

            {/* Right — Contact form */}
            <div className="contact-right">
              <div className="contact-form-header">
                <h2>Send Us a Message</h2>
                <p>Fill in the form below and we'll get back to you within 24 hours.</p>
              </div>

              {status === "success" ? (
                <div className="form-success">
                  <div className="success-icon"><i className="fa fa-check-circle"></i></div>
                  <h3>Message Sent!</h3>
                  <p>Thank you for reaching out. We'll respond to you within 24 hours.</p>
                  <button className="primary-btn" onClick={() => setStatus("idle")}>
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  {errorMsg && (
                    <div className="form-error-banner">
                      <i className="fa fa-exclamation-circle"></i> {errorMsg}
                    </div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Full Name <span className="required">*</span></label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="e.g. John Kamau"
                        value={form.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email Address <span className="required">*</span></label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number <span className="optional">(optional)</span></label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+254 7XX XXX XXX"
                        value={form.phone}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="subject">Subject <span className="required">*</span></label>
                      <select
                        id="subject"
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select a topic...</option>
                        <option>Course Enquiry</option>
                        <option>Programme Pricing</option>
                        <option>Enrolment / Registration</option>
                        <option>Corporate Training</option>
                        <option>General Enquiry</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message <span className="required">*</span></label>
                    <textarea
                      id="message"
                      name="message"
                      rows="6"
                      placeholder="Tell us how we can help you..."
                      value={form.message}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="primary-btn submit-btn"
                    disabled={status === "sending"}
                  >
                    {status === "sending" ? (
                      <><i className="fa fa-spinner fa-spin"></i> Sending...</>
                    ) : (
                      <><i className="fa fa-paper-plane"></i> Send Message</>
                    )}
                  </button>

                  <p className="form-note">
                    <i className="fa fa-lock"></i> Your information is kept private and never shared.
                  </p>
                </form>
              )}
            </div>
          </div>

        </div>
      </section>
    </>
  )
}

export default Contact
